import React, { useCallback, useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CloseIcon from '@mui/icons-material/Close';
import MapIcon from '@mui/icons-material/Map';
import ScheduleIcon from '@mui/icons-material/Schedule';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import InteractiveMapSearch from '../../dialogs/map/InteractiveMapSearch';
import { useTranslation } from 'react-i18next';
import { useAutoRefresh, useLocalStorage, useOffer } from '../../../hooks';
import { LoadingPlace, LoadingPlaceTypes } from '../../../models/booking';
import { formatDateTime } from '../../../utils/misc';
import CustomCard from '../../card/CustomCard';
import { AreaSearch, LocationParameter, PostalCodeSearch } from '../../../models/search';
import { CircularProgress } from '@mui/material';
import StandardDialog from '../../dialogs/base/standardDialog';
import OfferSearchForm from '../../../pages/loadSearch/OfferSearchForm';
import { getCountryCodeFromName } from '../../../models/country';
import { toast } from '../../../utils/toast';
import { TruckDetails, Vehicle } from '../../../types';
import { Refresh } from '@mui/icons-material';
import { MarketplaceNameEnum } from '../../../models/tenant';
import { MarketplaceChip } from '../../marketplaceChip';

export interface Route {
  startLocation: LocationParameter;
  destinationLocation: LocationParameter;
  active?: boolean;
  until?: string;
}
export interface FormData {
  id: number;
  mode: 'area' | 'postalCodeAreas';
  city?: string;
  country?: string;
  zip?: string;
  radius?: string;
  postal?: {
    postalCountry: string;
    postalCodes: string[];
  }[];
}

interface PostalCodeRegion {
  id: string;
  name: string;
  postalCodePrefix: string;
  country: string;
  isCountry?: boolean;
}
export interface FreightOffer {
  id: string;
  creationDateTime: string;
  contactPerson: {
    email: string;
    businessPhone?: string;
    fax?: string;
  };
  customer: {
    name: string;
  };
  freightDescription: string;
  price: { amount: number; currency: string };
  weight_t: number;
  loadingPlaces: LoadingPlace[];
  vehicleProperties: any;
  deeplink: string;
  searchId: number;
  additionalInformation?: any[];
  closedFreightExchangeSetting?: any;
  distance_km?: number | null;
  excludedCustomers?: any[];
  internalRemark?: string | null;
  length_m?: number;
  objectType?: string;
  paymentDueWithinDays?: number;
  publicRemark?: string | null;
  trackable?: boolean;
  useMessenger?: boolean;
  acceptQuotes?: boolean;
  externalPlatform?: string;
}

interface OfferSearchTableProps {
  selectedTruck: TruckDetails | null;
  vehicles?: Vehicle[];
  enabledMarketplaces?: MarketplaceNameEnum[];
}

const OfferSearchTable: React.FC<OfferSearchTableProps> = ({
  selectedTruck,
  vehicles,
  enabledMarketplaces = []
}) => {
  const { t } = useTranslation();

  const [mapSearchOpen, setMapSearchOpen] = useState(false);
  const [routes, setRoutes] = useLocalStorage<(Route & { marketplace?: string })[]>('searches', []);
  const [startFormData, setStartFormData] = useState<FormData>({ mode: 'area' } as FormData);
  const [endFormData, setEndFormData] = useState<FormData>({ mode: 'area' } as FormData);
  const [until, setUntil] = useState<string>('');
  const [marketplace, setMarketplace] = useState<string>(MarketplaceNameEnum.TimocomMarketplace);
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const hasInitialFetchDone = useRef(false);

  const {
    offers,
    loading: loadingOffers,
    Pagination,
    fetchOffers,
    setOffers
  } = useOffer();

  const { countDown, manualRefresh } = useAutoRefresh({
    countdownSeconds: 60,
    enabled: routes.filter(route => route.active).length > 0,
    onRefresh: () => {
      const activeRoute = routes.find((route) => route.active);
      if (activeRoute && activeRoute.marketplace !== 'WtransnetMarketplace') {
        handleRouteClick(activeRoute);
      }
    },
  });

  // Reset ref when component unmounts so it can run again on next mount
  useEffect(() => () => {
    hasInitialFetchDone.current = false;
  }, []);

  // Auto-fetch offers when page loads if search section is open and there's an active route
  useEffect(() => {
    // Only run on initial mount when search section is open and routes exist
    if (routes.length > 0 && !loadingOffers && !hasInitialFetchDone.current) {
      hasInitialFetchDone.current = true;
      const activeRoute = routes.find(route => route.active);
      if (activeRoute) {
        fetchOffersForRoute(activeRoute);
      } else if (routes.length > 0) {
        // If no active route but routes exist, activate the first one and fetch
        const firstRoute = routes[0];
        setRoutes((prevRoutes) => prevRoutes.map(r => ({ ...r, active: r === firstRoute })));
        fetchOffersForRoute(firstRoute);
      }
    }
  }, []); // Only run on mount

  const fetchOffersForRoute = useCallback(async (route: Route & { marketplace?: string }) => {
    if (!route) return;
    try {
      await fetchOffers({
        startLocation: route.startLocation,
        destinationLocation: route.destinationLocation,
        until: route.until,
        marketplace: route.marketplace,
      });
    } catch (_) {
      toast.error(t('Failed to fetch offers for route!'));
    }
  }, [t]);

  const buildArea = (city: string, country: string, postalCode: string, radius: string | number): AreaSearch => ({
    objectType: 'areaSearch',
    area: {
      address: {
        objectType: 'address',
        city,
        country: getCountryCodeFromName(country),
        postalCode,
      },
      size_km: parseInt(radius as string) || 200,
    },
  });

  const buildPostalCodeAreas = (country: string, codes: string | string[]): PostalCodeSearch => ({
    objectType: 'postalCodeAreasArray',
    postalCodeAreas: [
      {
        country,
        postalCodes: Array.isArray(codes) ? codes : codes.split(',')
          .map(c => c.substring(0, 2))
          .map((c) => c.trim())
          .filter(Boolean),
      },
    ],
  });

  const handleRouteClick = useCallback(
    (route: Route) => {
      // disable routes and enable clicked one
      setRoutes((routes) => routes.map(r => ({ ...r, active: r === route })));
      fetchOffersForRoute(route);
    },
    [fetchOffersForRoute, setRoutes]
  );

  const handleSearchCancel = () => {
    setStartFormData({} as FormData);
    setEndFormData({} as FormData);
    setValidationErrors([]);
    setSearchModalOpen(false);
  };

  const handleOfferSearchFormSubmit = () => {

    let startLocation: LocationParameter = {} as LocationParameter;
    let destinationLocation: LocationParameter = {} as LocationParameter;

    if (startFormData.mode === 'area') {
      if (!startFormData.country) {
        setValidationErrors([t('Country is required.')]);
        return;
      }
      startLocation = buildArea(startFormData.city || '', startFormData.country, startFormData.zip || '', startFormData.radius || '200');
    }

    if (startFormData.mode === 'postalCodeAreas' && startFormData.postal) {
      for (const postalCodeArea of startFormData.postal) {
        if (!postalCodeArea.postalCountry) {
          setValidationErrors([t('Postal country is required for start location.')]);
          return;
        }
      }
      startLocation = {
        objectType: 'postalCodeAreasArray',
        postalCodeAreas: startFormData.postal.map((postalCodeArea) => ({
          country: postalCodeArea.postalCountry || '',
          postalCodes: postalCodeArea.postalCodes || [],
        })),
      };
    }

    if (endFormData.mode === 'area') {
      if (!endFormData.country) {
        setValidationErrors([t('Country is required.')]);
        return;
      }
      destinationLocation = buildArea(endFormData.city || '', endFormData.country, endFormData.zip || '', endFormData.radius || '200');
    }

    if (endFormData.mode === 'postalCodeAreas' && endFormData.postal) {
      for (const postalCodeArea of endFormData.postal) {
        if (!postalCodeArea.postalCountry) {
          setValidationErrors([t('Postal country is required for destination location.')]);
          return;
        }
      }
      destinationLocation = {
        objectType: 'postalCodeAreasArray',
        postalCodeAreas: endFormData.postal.map((postalCodeArea) => ({
          country: postalCodeArea.postalCountry || '',
          postalCodes: postalCodeArea.postalCodes || [],
        })),
      };
    }

    const untilDate = new Date(until ?? '');
    if (isNaN(untilDate.getTime()) || untilDate < new Date()) {
      setValidationErrors([t('Invalid "until" date. Please enter a valid future date.')]);
      return;
    }

    const route = {
      startLocation,
      destinationLocation,
      active: true,
      until: untilDate.toISOString(),
      marketplace,
    };

    setRoutes((routes) => [...routes.map(route => ({ ...route, active: false })), route]);
    setStartFormData({} as FormData);
    setEndFormData({} as FormData);
    setValidationErrors([]);
    fetchOffersForRoute(route);
    setSearchModalOpen(false);
  };

  const handleRemoveRoute = (index: number) => {
    setRoutes((routes) => routes.map(route => route).filter((_, i) => i !== index));
    setOffers([]);
  };

  const handleMapSearch = (loadingRegions: PostalCodeRegion[], deliveryRegions: PostalCodeRegion[]) => {

    const firstLoading = loadingRegions[0];
    const firstDelivery = deliveryRegions[0];

    if (!firstLoading || !firstDelivery) {
      toast.error(t('Please select at least one loading and one delivery region on the map.'));
      return;
    }

    const startLocation = buildPostalCodeAreas(firstLoading.country || '', firstLoading.isCountry ? '' : firstLoading.postalCodePrefix);
    const destinationLocation = buildPostalCodeAreas(firstDelivery.country || '', firstDelivery.isCountry ? '' : firstDelivery.postalCodePrefix);

    const route = {
      startLocation,
      destinationLocation,
      active: true,
    };

    setRoutes((routes) => [...routes, route]);
  };

  const handleRowClick = (offer: FreightOffer) => {
    if (!offer.contactPerson.email) return;
    window.open(`mailto:${offer.contactPerson.email}`, '_blank');
  };

  const extractFirstTwoDigits = (postalCodes: string[] | string): string => {
    if (!postalCodes) return '';

    const codes = Array.isArray(postalCodes) ? postalCodes : [postalCodes];
    if (!codes) return '';

    return codes.map(code => {
      code = code.replace(/\D/g, '').substring(0, 2);
      if (code.length === 1) {
        code = '0' + code;
      }
      return code;
    }).join(',');
  };


  const label = (location: AreaSearch | PostalCodeSearch) => {
    if (!location) return t('Invalid Route');
    let label = '';

    if (location.objectType === 'areaSearch') {
      const areaSearch = location;
      const address = areaSearch.area.address;
      label += address ? `${address.country}` : '';
      label += address && address.postalCode ? extractFirstTwoDigits(address.postalCode) : '';
      label += address && address.city ? `(${address.city})` : '';
    } else {
      for (const [index, postalCodeArea] of location.postalCodeAreas?.entries() || []) {
        label += postalCodeArea.country;
        label += extractFirstTwoDigits(postalCodeArea.postalCodes) + (index < location.postalCodeAreas.length - 1 ? ', ' : '');
      }
    }
    return label;
  };


  return (
    <>
      <CustomCard sx={{ marginY: 0, marginBottom: 2 }}>
        <Box sx={styles.searchHeader}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6" sx={styles.searchTitle}>
              {t('Search Results')}
            </Typography>
            {routes.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ color: 'text.grey', fontSize: (theme: any) => theme.typography.table.body }}>
                  {t('Next refresh in: {{seconds}}s', { seconds: countDown })}
                </Typography>
                <IconButton
                  size="small"
                  onClick={manualRefresh}
                  sx={{
                    p: 0.5,
                    borderRadius: '50%',
                    bgcolor: 'background.white',
                  }}
                >
                  <Refresh sx={{ fontSize: (theme: any) => theme.typography.table.body }} />
                </IconButton>
                <Typography variant="body2" sx={{ color: 'text.grey', fontSize: (theme: any) => theme.typography.table.body }}>
                  • {routes.length} search{routes.length !== 1 ? 'es' : ''} {t('started')}
                </Typography>
              </Box>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              disabled
              onClick={() => setMapSearchOpen(true)}
              sx={{
                minWidth: 'auto',
                px: 2,
                py: 0.5,
                fontSize: (theme: any) => theme.typography.fontSizes.small,
                borderColor: 'text.black',
                color: 'text.black',
                '&:hover': {
                  borderColor: (theme: any) => theme.palette.text.darkGrey,
                  backgroundColor: 'background.grey',
                  color: 'text.darkGrey',
                },
              }}
              startIcon={<MapIcon sx={{ fontSize: (theme: any) => theme.typography.fontSizes.heading2 }} />}>
              {t('Map Search')}
            </Button>

            <Button
              variant="outlined"
              size="small"
              onClick={() => setSearchModalOpen(true)}
              sx={{
                minWidth: 'auto',
                px: 2,
                py: 0.5,
                fontSize: (theme: any) => theme.typography.fontSizes.small,
                borderColor: 'text.black',
                color: 'text.black',
                backgroundColor: 'background.white',
                '&:hover': {
                  borderColor: 'text.darkGrey',
                  backgroundColor: 'background.grey',
                  color: 'text.darkGrey',
                },
              }}>
              {t('Form Search')}
            </Button>
          </Box>
        </Box>

        {/* Search Tabs */}
        <Box sx={styles.searchTabsContainer}>
          {routes.length > 0 && routes.map((route, index) => (
            <Box
              key={index}
              sx={{
                ...styles.searchTab,
                ...(route.active ? styles.searchTabActive : {}),
              }}
              onClick={() => handleRouteClick(route)}>
              <Typography sx={{ fontSize: 'inherit', color: 'inherit' }}>
                {label(route.startLocation) + ' → ' + label(route.destinationLocation)}
              </Typography>
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); handleRemoveRoute(index); }}
                sx={styles.searchTabClose}>
                <CloseIcon sx={{ fontSize: (theme: any) => theme.typography.fontSizes.body, color: 'inherit' }} />
              </IconButton>
              {loadingOffers && route.active ? <CircularProgress sx={{ color: 'background.lightGrey', ml: 1 }} size={16} /> : ''}
            </Box>
          ))}
        </Box>

        {/* Freight Offers Table */}
        <Box sx={{ mt: 2 }}>
          {loadingOffers ? (
            <Box sx={{ textAlign: 'center', py: 4, color: 'text.grey' }}>
              <Typography variant="body2" sx={{ fontSize: (theme: any) => theme.typography.table.body }}>{t('Loading offers...')}</Typography>
            </Box>
          ) : offers.length > 0 ? (
            <>
              <TableContainer sx={styles.tableContainer}>
                <Table sx={styles.table}>
                  <TableHead sx={styles.tableHead}>
                    <TableRow>
                      <TableCell sx={styles.tableHeaderCell}>{t('Company')}</TableCell>
                      <TableCell sx={{ ...styles.tableHeaderCell, display: { xs: 'none', sm: 'table-cell' } }}>{t('Contact')}</TableCell>
                      <TableCell sx={styles.tableHeaderCell}>{t('Route')}</TableCell>
                      <TableCell sx={styles.tableHeaderCell}>{t('Timeline')}</TableCell>
                      <TableCell sx={styles.tableHeaderCell}>{t('W/L')}</TableCell>
                      <TableCell sx={styles.tableHeaderCell}>{t('Price')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {offers.map((offer) => {
                      // Get all loading and delivery places
                      const loadingPlaces = offer.loadingPlaces.filter((place) => place.loadingType === LoadingPlaceTypes.LOADING);
                      const deliveryPlaces = offer.loadingPlaces.filter((place) => place.loadingType === LoadingPlaceTypes.UNLOADING);

                      // Get first loading place and last delivery place
                      const firstLoadingPlace = loadingPlaces[0];
                      const lastDeliveryPlace = deliveryPlaces[deliveryPlaces.length - 1];

                      // Format loading place with count
                      const formatLoadingPlace = (place: LoadingPlace | undefined, totalCount: number) => {
                        if (!place) return 'N/A';
                        const postalCodePrefix = place.address.postalCode?.slice(0, 2) || '';
                        const base = `${place.address.country}-${postalCodePrefix} ${place.address.city}`;
                        if (totalCount > 1) {
                          return `${base} (+${totalCount - 1})`;
                        }
                        return base;
                      };

                      // Format delivery place with count
                      const formatDeliveryPlace = (place: LoadingPlace | undefined, totalCount: number) => {
                        if (!place) return 'N/A';
                        const postalCodePrefix = place.address.postalCode?.slice(0, 2) || '';
                        const base = `${place.address.country}-${postalCodePrefix} ${place.address.city}`;
                        if (totalCount > 1) {
                          return `${base} (+${totalCount - 1})`;
                        }
                        return base;
                      };

                      // Format all places for tooltip
                      const formatAllPlacesForTooltip = () => {
                        const loadingList = loadingPlaces.map((place, index) => {
                          const postalCodePrefix = place.address.postalCode?.slice(0, 2) || '';
                          return `${index + 1}. ${place.address.country}-${postalCodePrefix} ${place.address.city}`;
                        }).join('\n');

                        const deliveryList = deliveryPlaces.map((place, index) => {
                          const postalCodePrefix = place.address.postalCode?.slice(0, 2) || '';
                          return `${index + 1}. ${place.address.country}-${postalCodePrefix} ${place.address.city}`;
                        }).join('\n');

                        return `Loading Places (${loadingPlaces.length}):\n${loadingList || 'N/A'}\n\nDelivery Places (${deliveryPlaces.length}):\n${deliveryList || 'N/A'}`;
                      };

                      return (
                        <TableRow
                          key={offer.id}
                          sx={{
                            ...styles.tableRow,
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: 'background.grey',
                            },
                          }}
                          onClick={() => handleRowClick(offer)}
                        >
                          <TableCell sx={styles.tableCell}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                              <Typography variant="body2" sx={{ fontSize: (theme: any) => theme.typography.table.body }}>
                                {offer.customer?.name || t('Unknown Company')}
                              </Typography>
                              <MarketplaceChip
                                externalPlatform={offer.externalPlatform ?? routes.find((r) => r.active)?.marketplace}
                                size="xs"
                              />
                            </Box>
                            <Box
                              sx={{
                                display: { xs: 'flex', sm: 'none' },
                                alignItems: 'center',
                                gap: 0.5,
                              }}
                            >
                              {offer.contactPerson.email && (
                                <Tooltip title={t('Email')}><EmailOutlinedIcon sx={{ fontSize: '0.875rem', color: 'text.secondary' }} /></Tooltip>
                              )}
                              {offer.useMessenger && (
                                <Tooltip title="Timocom Messenger"><ChatBubbleOutlineIcon sx={{ fontSize: '0.875rem', color: 'info.main' }} /></Tooltip>
                              )}
                              {!offer.contactPerson.email && !offer.useMessenger && (
                                <Tooltip title={t('No available channel')}><WarningAmberOutlinedIcon sx={{ fontSize: '0.875rem' }} color="warning" /></Tooltip>
                              )}
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: (theme: any) => theme.typography.table.small,
                                  color: 'text.grey',
                                }}
                              >
                                {offer.contactPerson.email}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ ...styles.tableCell, display: { xs: 'none', sm: 'table-cell' } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              {offer.contactPerson.email && (
                                <Tooltip title={t('Email')}><EmailOutlinedIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} /></Tooltip>
                              )}
                              {offer.useMessenger && (
                                <Tooltip title="Timocom Messenger"><ChatBubbleOutlineIcon sx={{ fontSize: '1rem', color: 'info.main' }} /></Tooltip>
                              )}
                              {!offer.contactPerson.email && !offer.useMessenger && (
                                <Tooltip title={t('No available channel')}><WarningAmberOutlinedIcon sx={{ fontSize: '1rem' }} color="warning" /></Tooltip>
                              )}
                              <Typography variant="body2" sx={{ fontSize: (theme: any) => theme.typography.table.body }}>
                                {offer.contactPerson.email}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={styles.tableCell}>
                            <Tooltip
                              title={
                                <Box sx={{ whiteSpace: 'pre-line', fontSize: (theme: any) => theme.typography.table.body }}>
                                  {formatAllPlacesForTooltip()}
                                </Box>
                              }
                              arrow
                              placement="top"
                            >
                              <Box sx={{ cursor: 'help' }}>
                                <Typography variant="body2" sx={{ fontSize: (theme: any) => theme.typography.table.body }}>
                                  {formatLoadingPlace(firstLoadingPlace, loadingPlaces.length)}
                                </Typography>
                                <Typography variant="body2" sx={{ fontSize: (theme: any) => theme.typography.table.small, color: 'text.grey' }}>
                                  {formatDeliveryPlace(lastDeliveryPlace, deliveryPlaces.length)}
                                </Typography>
                              </Box>
                            </Tooltip>
                          </TableCell>
                          <TableCell sx={styles.tableCell}>
                            {/* Mobile: Icons with tooltips */}
                            <Box sx={{ display: { xs: 'flex', sm: 'none' }, gap: 1, justifyContent: 'center' }}>
                              <Tooltip
                                title={firstLoadingPlace ? `Loading: ${formatDateTime(firstLoadingPlace.earliestLoadingDate, true)} ${firstLoadingPlace.startTime ?? ''}${firstLoadingPlace.endTime ? '-' + firstLoadingPlace.endTime : ''}` : `Loading: ${t('N/A')}`}
                                arrow
                              >
                                <ScheduleIcon sx={{ fontSize: 18, color: 'text.primary' }} />
                              </Tooltip>
                              <Tooltip
                                title={lastDeliveryPlace ? `Delivery: ${formatDateTime(lastDeliveryPlace.earliestLoadingDate, true)} ${lastDeliveryPlace.startTime ?? ''}${lastDeliveryPlace.endTime ? '-' + lastDeliveryPlace.endTime : ''}` : `Delivery: ${t('N/A')}`}
                                arrow
                              >
                                <LocalShippingIcon sx={{ fontSize: 18, color: 'text.grey' }} />
                              </Tooltip>
                            </Box>
                            {/* Desktop: Full text */}
                            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                              <Typography variant="body2" sx={{ fontSize: (theme: any) => theme.typography.table.body }}>
                                {firstLoadingPlace ? `Loading: ${formatDateTime(firstLoadingPlace.earliestLoadingDate, true)} ${firstLoadingPlace.startTime ?? ''}${firstLoadingPlace.endTime ? '-' + firstLoadingPlace.endTime : ''}` : `Loading: ${t('N/A')}`}
                              </Typography>
                              <Typography variant="body2" sx={{ fontSize: (theme: any) => theme.typography.table.small, color: 'text.grey' }}>
                                {lastDeliveryPlace ? `Delivery: ${formatDateTime(lastDeliveryPlace.earliestLoadingDate, true)} ${lastDeliveryPlace.startTime ?? ''}${lastDeliveryPlace.endTime ? '-' + lastDeliveryPlace.endTime : ''}` : `Delivery: ${t('N/A')}`}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={styles.tableCell}>
                            <Box>
                              <Typography variant="body2" sx={{ fontSize: (theme: any) => theme.typography.table.body, color: offer.weight_t > 25 ? 'text.red' : 'inherit' }}>
                                {offer.weight_t} t
                              </Typography>
                              <Typography variant="body2" sx={{ fontSize: (theme: any) => theme.typography.table.small, color: offer.length_m && offer.length_m > 13.60 ? 'text.red' : 'text.grey' }}>
                                {offer.length_m ? `${offer.length_m} m` : 'N/A'}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={styles.tableCell}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                              <Typography variant="body2" sx={{ fontSize: (theme: any) => theme.typography.table.body, fontWeight: 600 }}>
                                {offer.price?.amount ?? 'N/A'}
                              </Typography>
                              <Typography variant="body2" sx={{ fontSize: (theme: any) => theme.typography.table.small, color: 'text.grey' }}>
                                {offer.price?.currency ?? ''}
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4, height: '70vh' }}>
              <Typography sx={{ color: 'text.grey', fontSize: (theme: any) => theme.typography.table.body }} variant="body2">{t('No offers available for this route')}</Typography>
              <Typography sx={{ color: 'text.grey', fontSize: (theme: any) => theme.typography.table.small }} variant="caption">
                {t('Try selecting a different route or creating a new search')}
              </Typography>
            </Box>
          )}
        </Box>
        {Pagination}
      </CustomCard>

      <StandardDialog
        open={searchModalOpen}
        onClose={handleSearchCancel}
        title={t('Search offers')}
        maxWidth="md"
        width={{ xs: '100%', sm: '90%', md: '700px' }}
        height={{ xs: '95vh', sm: '90vh', md: '80vh' }}
        primaryAction={{
          label: t('Search'),
          onClick: handleOfferSearchFormSubmit
        }}
        secondaryAction={{
          label: t('Cancel'),
          onClick: handleSearchCancel
        }}
        error={validationErrors.length > 0 ? validationErrors.join(', ') : null}
        contentSx={{ p: { xs: 1.5, sm: 2, md: 2 } }}
      >
        <OfferSearchForm
          startFormData={startFormData}
          endFormData={endFormData}
          until={until}
          marketplace={marketplace}
          enabledMarketplaces={enabledMarketplaces}
          errors={validationErrors}
          selectedTruck={selectedTruck}
          vehicles={vehicles}
          setStartFormData={setStartFormData}
          setEndFormData={setEndFormData}
          setUntil={setUntil}
          setMarketplace={setMarketplace}
          onSearch={handleOfferSearchFormSubmit}
        />
      </StandardDialog>

      {/* Interactive Map Search Modal */}
      <InteractiveMapSearch
        open={mapSearchOpen}
        onClose={() => setMapSearchOpen(false)}
        onSearch={(loadingRegions: any[], deliveryRegions: any[]) => {
          if (handleMapSearch) {
            handleMapSearch(loadingRegions, deliveryRegions);
          }
        }}
      />
    </>
  );
};

export default OfferSearchTable;

const styles = {
  searchHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    mb: 2,
  },
  searchTitle: {
    fontWeight: 600,
    fontSize: (theme: any) => theme.typography.fontSizes.heading,
    color: 'text.dark',
  },
  searchTabsContainer: {
    display: 'flex',
    gap: 1,
    mb: 2,
    flexWrap: 'wrap',
  },
  searchTab: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    p: 1,
    borderRadius: 1,
    border: '1px solid border.medium',
    bgcolor: 'background.white',
    color: 'text.grey',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontSize: (theme: any) => theme.typography.fontSizes.body,
    '&:hover': {
      bgcolor: 'background.grey',
      borderColor: 'border.medium',
      color: 'text.darkGrey',
    },
    '& .MuiTypography-root': {
      color: 'inherit',
    },
  },
  searchTabActive: {
    bgcolor: 'background.shadowBlack',
    color: 'text.white',
    borderColor: 'text.black',
    '&:hover': {
      bgcolor: 'background.black',
      borderColor: 'text.black',
    },
    '& .MuiTypography-root': {
      color: 'text.white',
    },
  },
  searchTabClose: {
    ml: 0,
    p: 0,
    minWidth: 'auto',
    width: 20,
    height: 20,
    borderRadius: '50%',
    color: 'inherit',
    '&:hover': {
      bgcolor: 'background.shadowBlack',
      color: 'text.white',
    },
  },
  tableContainer: {
    borderRadius: 1.5,
    border: '1px solid border.default',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08), 0 2px 4px rgba(0,0,0,0.06)',
    overflow: 'auto',
    transition: 'all 0.3s ease-in-out',
    height: '800px',
    opacity: 1,
    transform: 'translateY(0)',
    // Use thin scrollbar for Firefox (less obtrusive)
    scrollbarWidth: 'thin',
    // Hide scrollbar by default (Webkit)
    '&::-webkit-scrollbar': {
      width: '0px',
      transition: 'width 0.3s ease-in-out',
    },
    // Show scrollbar on hover (when scrolling/interacting)
    '&:hover::-webkit-scrollbar': {
      width: '8px',
    },
    // Show scrollbar when not focused
    '&:not(:focus-within)::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      borderRadius: '4px',
    },
  },
  table: {
    minWidth: 650,
  },
  tableHead: {
    bgcolor: 'background.white',
    '& .MuiTableCell-root': {
      padding: { xs: '4px 2px', sm: '4px', md: '4px', lg: '6px' },
    },
    '& .MuiTableCell-root:first-of-type': {
      paddingLeft: { xs: '8px', sm: '12px', md: '16px', lg: '20px' },
    }
  },
  tableHeaderCell: {
    fontWeight: 600,
    backgroundColor: 'background.grey',
    padding: { xs: '4px 2px', sm: '4px', md: '4px', lg: '6px' },
    color: 'text.darkGrey',
    borderBottom: '2px solid border.default',
    fontSize: (theme: any) => theme.typography.table.header,
    textAlign: 'center',
    '&:first-of-type': {
      paddingLeft: { xs: '8px', sm: '12px', md: '16px', lg: '20px' },
    },
  },
  tableRow: {
    '&:nth-of-type(odd)': {
      backgroundColor: 'background.white',
    },
  },
  tableCell: {
    borderBottom: '1px solid border.light',
    padding: { xs: '4px 6px', sm: '6px 10px', md: '8px 15px', lg: '10px 18px' },
    lineHeight: { xs: 1.2, sm: 1.3, md: 1.4, lg: 1.5 },
    textAlign: 'center',
    '&:first-of-type': {
      paddingLeft: { xs: '8px', sm: '12px', md: '16px', lg: '20px' },
    },
    '& .MuiTypography-root': {
      margin: 0,
      lineHeight: 'inherit',
    },
    '& .MuiBox-root': {
      margin: 0,
      lineHeight: 'inherit',
    },
  },
};
